

import { Direction, Directionality } from '@angular/cdk/bidi';
import { CdkOverlayOrigin, ConnectedOverlayPositionChange } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  Directive,
  ElementRef,
  EventEmitter,
  forwardRef,
  Host,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  QueryList,
  Renderer2,
  SimpleChanges,
  SkipSelf,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { BehaviorSubject, combineLatest, merge, Subject } from 'rxjs';
import { map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { MwcNoAnimationDirective } from 'mwc/core/no-animation';
import { getPlacementName, POSITION_MAP, POSITION_TYPE_HORIZONTAL } from 'mwc/core/overlay';
import { BooleanInput, MwcSafeAny } from 'mwc/core/types';
import { InputBoolean } from 'mwc/core/util';

import { MwcMenuItemDirective } from './menu-item.directive';
import { MenuService } from './menu.service';
import { MwcIsMenuInsideDropDownToken, MwcMenuServiceLocalToken } from './menu.token';
import { MwcMenuModeType, MwcMenuThemeType } from './menu.types';
import { MwcSubmenuService } from './submenu.service';
import { collapseMotion, zoomBigMotion, slideMotion } from 'mwc/core/animation';

const listOfVerticalPositions = [
  POSITION_MAP.rightTop,
  POSITION_MAP.right,
  POSITION_MAP.rightBottom,
  POSITION_MAP.leftTop,
  POSITION_MAP.left,
  POSITION_MAP.leftBottom
];
const listOfHorizontalPositions = [
  POSITION_MAP.bottomLeft,
  POSITION_MAP.bottomRight,
  POSITION_MAP.topRight,
  POSITION_MAP.topLeft
];

export function MenuServiceFactory(
  serviceInsideDropDown: MenuService,
  serviceOutsideDropDown: MenuService
): MenuService {
  return serviceInsideDropDown ? serviceInsideDropDown : serviceOutsideDropDown;
}
export function MenuDropDownTokenFactory(isMenuInsideDropDownToken: boolean): boolean {
  return isMenuInsideDropDownToken ? isMenuInsideDropDownToken : false;
}

@Component({
  selector: '[mwc-submenu-inline-child]',
  animations: [collapseMotion],
  exportAs: 'mwcSubmenuInlineChild',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: ` <ng-template [ngTemplateOutlet]="templateOutlet"></ng-template> `,
  host: {
    class: 'mwc-menu mwc-menu-inline mwc-menu-sub',
    '[class.mwc-menu-rtl]': `dir === 'rtl'`,
    '[@collapseMotion]': 'expandState'
  }
})
export class MwcSubmenuInlineChildComponent implements OnDestroy, OnInit, OnChanges {
  @Input() templateOutlet: TemplateRef<MwcSafeAny> | null = null;
  @Input() menuClass: string = '';
  @Input() mode: MwcMenuModeType = 'vertical';
  @Input() mwcOpen = false;
  listOfCacheClassName: string[] = [];
  expandState = 'collapsed';
  dir: Direction = 'ltr';
  private destroy$ = new Subject<void>();

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    @Optional() private directionality: Directionality
  ) {}

  calcMotionState(): void {
    if (this.mwcOpen) {
      this.expandState = 'expanded';
    } else {
      this.expandState = 'collapsed';
    }
  }
  ngOnInit(): void {
    this.calcMotionState();

    this.dir = this.directionality.value;
    this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe((direction: Direction) => {
      this.dir = direction;
    });
  }
  ngOnChanges(changes: SimpleChanges): void {
    const { mode, mwcOpen, menuClass } = changes;
    if (mode || mwcOpen) {
      this.calcMotionState();
    }
    if (menuClass) {
      if (this.listOfCacheClassName.length) {
        this.listOfCacheClassName
          .filter(item => !!item)
          .forEach(className => {
            this.renderer.removeClass(this.elementRef.nativeElement, className);
          });
      }
      if (this.menuClass) {
        this.listOfCacheClassName = this.menuClass.split(' ');
        this.listOfCacheClassName
          .filter(item => !!item)
          .forEach(className => {
            this.renderer.addClass(this.elementRef.nativeElement, className);
          });
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

@Component({
  selector: '[mwc-submenu-none-inline-child]',
  exportAs: 'mwcSubmenuNoneInlineChild',
  encapsulation: ViewEncapsulation.None,
  animations: [zoomBigMotion, slideMotion],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      [class.mwc-dropdown-menu]="isMenuInsideDropDown"
      [class.mwc-menu]="!isMenuInsideDropDown"
      [class.mwc-dropdown-menu-vertical]="isMenuInsideDropDown"
      [class.mwc-menu-vertical]="!isMenuInsideDropDown"
      [class.mwc-dropdown-menu-sub]="isMenuInsideDropDown"
      [class.mwc-menu-sub]="!isMenuInsideDropDown"
      [class.mwc-menu-rtl]="dir === 'rtl'"
      [ngClass]="menuClass"
    >
      <ng-template [ngTemplateOutlet]="templateOutlet"></ng-template>
    </div>
  `,
  host: {
    class: 'mwc-menu-submenu mwc-menu-submenu-popup',
    '[class.mwc-menu-light]': "theme === 'light'",
    '[class.mwc-menu-dark]': "theme === 'dark'",
    '[class.mwc-menu-submenu-placement-bottom]': "mode === 'horizontal'",
    '[class.mwc-menu-submenu-placement-right]': "mode === 'vertical' && position === 'right'",
    '[class.mwc-menu-submenu-placement-left]': "mode === 'vertical' && position === 'left'",
    '[class.mwc-menu-submenu-rtl]': 'dir ==="rtl"',
    '[@slideMotion]': 'expandState',
    '[@zoomBigMotion]': 'expandState',
    '(mouseenter)': 'setMouseState(true)',
    '(mouseleave)': 'setMouseState(false)'
  }
})
export class MwcSubmenuNoneInlineChildComponent implements OnDestroy, OnInit, OnChanges {
  @Input() menuClass: string = '';
  @Input() theme: MwcMenuThemeType = 'light';
  @Input() templateOutlet: TemplateRef<MwcSafeAny> | null = null;
  @Input() isMenuInsideDropDown = false;
  @Input() mode: MwcMenuModeType = 'vertical';
  @Input() position = 'right';
  @Input() mwcDisabled = false;
  @Input() mwcOpen = false;
  @Output() readonly subMenuMouseState = new EventEmitter<boolean>();

  constructor(@Optional() private directionality: Directionality) {}

  setMouseState(state: boolean): void {
    if (!this.mwcDisabled) {
      this.subMenuMouseState.next(state);
    }
  }
  expandState = 'collapsed';
  dir: Direction = 'ltr';
  private destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  calcMotionState(): void {
    if (this.mwcOpen) {
      if (this.mode === 'horizontal') {
        this.expandState = 'bottom';
      } else if (this.mode === 'vertical') {
        this.expandState = 'active';
      }
    } else {
      this.expandState = 'collapsed';
    }
  }
  ngOnInit(): void {
    this.calcMotionState();

    this.dir = this.directionality.value;
    this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe((direction: Direction) => {
      this.dir = direction;
    });
  }
  ngOnChanges(changes: SimpleChanges): void {
    const { mode, mwcOpen } = changes;
    if (mode || mwcOpen) {
      this.calcMotionState();
    }
  }
}


@Component({
  selector: '[mwc-submenu]',
  exportAs: 'mwcSubmenu',
  providers: [MwcSubmenuService],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  preserveWhitespaces: false,
  template: `
    <div
      mwc-submenu-title
      cdkOverlayOrigin
      #origin="cdkOverlayOrigin"
      [mwcIcon]="mwcIcon"
      [mwcTitle]="mwcTitle"
      [mode]="mode"
      [mwcDisabled]="mwcDisabled"
      [isMenuInsideDropDown]="isMenuInsideDropDown"
      [paddingLeft]="mwcPaddingLeft || inlinePaddingLeft"
      (subMenuMouseState)="setMouseEnterState($event)"
      (toggleSubMenu)="toggleSubMenu()"
    >
      <ng-content select="[title]" *ngIf="!mwcTitle"></ng-content>
    </div>
    <div
      *ngIf="mode === 'inline'; else nonInlineTemplate"
      mwc-submenu-inline-child
      [mode]="mode"
      [mwcOpen]="mwcOpen"
      [@.disabled]="!!noAnimation?.mwcNoAnimation"
      [mwcNoAnimation]="noAnimation?.mwcNoAnimation"
      [menuClass]="mwcMenuClassName"
      [templateOutlet]="subMenuTemplate"
    ></div>
    <ng-template #nonInlineTemplate>
      <ng-template
        cdkConnectedOverlay
        (positionChange)="onPositionChange($event)"
        [cdkConnectedOverlayPositions]="overlayPositions"
        [cdkConnectedOverlayOrigin]="origin"
        [cdkConnectedOverlayWidth]="triggerWidth!"
        [cdkConnectedOverlayOpen]="mwcOpen"
        [cdkConnectedOverlayTransformOriginOn]="'.mwc-menu-submenu'"
      >
        <div
          mwc-submenu-none-inline-child
          [theme]="theme"
          [mode]="mode"
          [mwcOpen]="mwcOpen"
          [position]="position"
          [mwcDisabled]="mwcDisabled"
          [isMenuInsideDropDown]="isMenuInsideDropDown"
          [templateOutlet]="subMenuTemplate"
          [menuClass]="mwcMenuClassName"
          [@.disabled]="!!noAnimation?.mwcNoAnimation"
          [mwcNoAnimation]="noAnimation?.mwcNoAnimation"
          (subMenuMouseState)="setMouseEnterState($event)"
        ></div>
      </ng-template>
    </ng-template>

    <ng-template #subMenuTemplate>
      <ng-content></ng-content>
    </ng-template>
  `,
  host: {
    '[class.mwc-dropdown-menu-submenu]': `isMenuInsideDropDown`,
    '[class.mwc-dropdown-menu-submenu-disabled]': `isMenuInsideDropDown && mwcDisabled`,
    '[class.mwc-dropdown-menu-submenu-open]': `isMenuInsideDropDown && mwcOpen`,
    '[class.mwc-dropdown-menu-submenu-selected]': `isMenuInsideDropDown && isSelected`,
    '[class.mwc-dropdown-menu-submenu-vertical]': `isMenuInsideDropDown && mode === 'vertical'`,
    '[class.mwc-dropdown-menu-submenu-horizontal]': `isMenuInsideDropDown && mode === 'horizontal'`,
    '[class.mwc-dropdown-menu-submenu-inline]': `isMenuInsideDropDown && mode === 'inline'`,
    '[class.mwc-dropdown-menu-submenu-active]': `isMenuInsideDropDown && isActive`,
    '[class.mwc-menu-submenu]': `!isMenuInsideDropDown`,
    '[class.mwc-menu-submenu-disabled]': `!isMenuInsideDropDown && mwcDisabled`,
    '[class.mwc-menu-submenu-open]': `!isMenuInsideDropDown && mwcOpen`,
    '[class.mwc-menu-submenu-selected]': `!isMenuInsideDropDown && isSelected`,
    '[class.mwc-menu-submenu-vertical]': `!isMenuInsideDropDown && mode === 'vertical'`,
    '[class.mwc-menu-submenu-horizontal]': `!isMenuInsideDropDown && mode === 'horizontal'`,
    '[class.mwc-menu-submenu-inline]': `!isMenuInsideDropDown && mode === 'inline'`,
    '[class.mwc-menu-submenu-active]': `!isMenuInsideDropDown && isActive`,
    '[class.mwc-menu-submenu-rtl]': `dir === 'rtl'`
  }
})
export class MwcSubMenuComponent implements OnInit, OnDestroy, AfterContentInit, OnChanges {
  static ngAcceptInputType_mwcOpen: BooleanInput;
  static ngAcceptInputType_mwcDisabled: BooleanInput;

  @Input() mwcMenuClassName: string = '';
  @Input() mwcPaddingLeft: number | null = null;
  @Input() mwcTitle: string | TemplateRef<void> | null = null;
  @Input() mwcIcon: string | null = null;
  @Input() @InputBoolean() mwcOpen = false;
  @Input() @InputBoolean() mwcDisabled = false;
  @Input() mwcPlacement: POSITION_TYPE_HORIZONTAL = 'bottomLeft';
  @Output() readonly mwcOpenChange: EventEmitter<boolean> = new EventEmitter();
  @ViewChild(CdkOverlayOrigin, { static: true, read: ElementRef }) cdkOverlayOrigin: ElementRef | null = null;
  // fix errors about circular dependency
  // Can't construct a query for the property ... since the query selector wasn't defined"
  @ContentChildren(forwardRef(() => MwcSubMenuComponent), { descendants: true })
  listOfMwcSubMenuComponent: QueryList<MwcSubMenuComponent> | null = null;
  @ContentChildren(MwcMenuItemDirective, { descendants: true })
  listOfMwcMenuItemDirective: QueryList<MwcMenuItemDirective> | null = null;
  private level = this.mwcSubmenuService.level;
  private destroy$ = new Subject<void>();
  position = 'right';
  triggerWidth: number | null = null;
  theme: MwcMenuThemeType = 'light';
  mode: MwcMenuModeType = 'vertical';
  inlinePaddingLeft: number | null = null;
  overlayPositions = listOfVerticalPositions;
  isSelected = false;
  isActive = false;
  dir: Direction = 'ltr';

  /** set the submenu host open status directly **/
  setOpenStateWithoutDebounce(open: boolean): void {
    this.mwcSubmenuService.setOpenStateWithoutDebounce(open);
  }

  toggleSubMenu(): void {
    this.setOpenStateWithoutDebounce(!this.mwcOpen);
  }

  setMouseEnterState(value: boolean): void {
    this.isActive = value;
    if (this.mode !== 'inline') {
      this.mwcSubmenuService.setMouseEnterTitleOrOverlayState(value);
    }
  }

  setTriggerWidth(): void {
    if (
      this.mode === 'horizontal' &&
      this.platform.isBrowser &&
      this.cdkOverlayOrigin &&
      this.mwcPlacement === 'bottomLeft'
    ) {
      /** TODO: fast dom **/
      this.triggerWidth = this.cdkOverlayOrigin!.nativeElement.getBoundingClientRect().width;
    }
  }

  onPositionChange(position: ConnectedOverlayPositionChange): void {
    const placement = getPlacementName(position);
    if (placement === 'rightTop' || placement === 'rightBottom' || placement === 'right') {
      this.position = 'right';
    } else if (placement === 'leftTop' || placement === 'leftBottom' || placement === 'left') {
      this.position = 'left';
    }
  }

  constructor(
    public mwcMenuService: MenuService,
    private cdr: ChangeDetectorRef,
    public mwcSubmenuService: MwcSubmenuService,
    private platform: Platform,
    @Inject(MwcIsMenuInsideDropDownToken) public isMenuInsideDropDown: boolean,
    @Optional() private directionality: Directionality,
    @Host() @Optional() public noAnimation?: MwcNoAnimationDirective
  ) {}

  ngOnInit(): void {
    /** submenu theme update **/
    this.mwcMenuService.theme$.pipe(takeUntil(this.destroy$)).subscribe(theme => {
      this.theme = theme;
      this.cdr.markForCheck();
    });
    /** submenu mode update **/
    this.mwcSubmenuService.mode$.pipe(takeUntil(this.destroy$)).subscribe(mode => {
      this.mode = mode;
      if (mode === 'horizontal') {
        this.overlayPositions = [POSITION_MAP[this.mwcPlacement], ...listOfHorizontalPositions];
      } else if (mode === 'vertical') {
        this.overlayPositions = listOfVerticalPositions;
      }
      this.cdr.markForCheck();
    });
    /** inlineIndent update **/
    combineLatest([this.mwcSubmenuService.mode$, this.mwcMenuService.inlineIndent$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([mode, inlineIndent]) => {
        this.inlinePaddingLeft = mode === 'inline' ? this.level * inlineIndent : null;
        this.cdr.markForCheck();
      });
    /** current submenu open status **/
    this.mwcSubmenuService.isCurrentSubMenuOpen$.pipe(takeUntil(this.destroy$)).subscribe(open => {
      this.isActive = open;
      if (open !== this.mwcOpen) {
        this.setTriggerWidth();
        this.mwcOpen = open;
        this.mwcOpenChange.emit(this.mwcOpen);
        this.cdr.markForCheck();
      }
    });

    this.dir = this.directionality.value;
    this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe((direction: Direction) => {
      this.dir = direction;
      this.cdr.markForCheck();
    });
  }

  ngAfterContentInit(): void {
    this.setTriggerWidth();
    const listOfMwcMenuItemDirective = this.listOfMwcMenuItemDirective;
    const changes = listOfMwcMenuItemDirective!.changes;
    const mergedObservable = merge(...[changes, ...listOfMwcMenuItemDirective!.map(menu => menu.selected$)]);
    changes
      .pipe(
        startWith(listOfMwcMenuItemDirective),
        switchMap(() => mergedObservable),
        startWith(true),
        map(() => listOfMwcMenuItemDirective!.some(e => e.mwcSelected)),
        takeUntil(this.destroy$)
      )
      .subscribe(selected => {
        this.isSelected = selected;
        this.cdr.markForCheck();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { mwcOpen } = changes;
    if (mwcOpen) {
      this.mwcSubmenuService.setOpenStateWithoutDebounce(this.mwcOpen);
      this.setTriggerWidth();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

@Directive({
  selector: '[mwc-menu]',
  exportAs: 'mwcMenu',
  providers: [
    {
      provide: MwcMenuServiceLocalToken,
      useClass: MenuService
    },
    /** use the top level service **/
    {
      provide: MenuService,
      useFactory: MenuServiceFactory,
      deps: [[new SkipSelf(), new Optional(), MenuService], MwcMenuServiceLocalToken]
    },
    /** check if menu inside dropdown-menu component **/
    {
      provide: MwcIsMenuInsideDropDownToken,
      useFactory: MenuDropDownTokenFactory,
      deps: [[new SkipSelf(), new Optional(), MwcIsMenuInsideDropDownToken]]
    }
  ],
  host: {
    '[class.mwc-dropdown-menu]': `isMenuInsideDropDown`,
    '[class.mwc-dropdown-menu-root]': `isMenuInsideDropDown`,
    '[class.mwc-dropdown-menu-light]': `isMenuInsideDropDown && mwcTheme === 'light'`,
    '[class.mwc-dropdown-menu-dark]': `isMenuInsideDropDown && mwcTheme === 'dark'`,
    '[class.mwc-dropdown-menu-vertical]': `isMenuInsideDropDown && actualMode === 'vertical'`,
    '[class.mwc-dropdown-menu-horizontal]': `isMenuInsideDropDown && actualMode === 'horizontal'`,
    '[class.mwc-dropdown-menu-inline]': `isMenuInsideDropDown && actualMode === 'inline'`,
    '[class.mwc-dropdown-menu-inline-collapsed]': `isMenuInsideDropDown && mwcInlineCollapsed`,
    '[class.mwc-menu]': `!isMenuInsideDropDown`,
    '[class.mwc-menu-root]': `!isMenuInsideDropDown`,
    '[class.mwc-menu-light]': `!isMenuInsideDropDown && mwcTheme === 'light'`,
    '[class.mwc-menu-dark]': `!isMenuInsideDropDown && mwcTheme === 'dark'`,
    '[class.mwc-menu-vertical]': `!isMenuInsideDropDown && actualMode === 'vertical'`,
    '[class.mwc-menu-horizontal]': `!isMenuInsideDropDown && actualMode === 'horizontal'`,
    '[class.mwc-menu-inline]': `!isMenuInsideDropDown && actualMode === 'inline'`,
    '[class.mwc-menu-inline-collapsed]': `!isMenuInsideDropDown && mwcInlineCollapsed`,
    '[class.mwc-menu-rtl]': `dir === 'rtl'`
  }
})
export class MwcMenuDirective implements AfterContentInit, OnInit, OnChanges, OnDestroy {
  static ngAcceptInputType_mwcInlineCollapsed: BooleanInput;
  static ngAcceptInputType_mwcSelectable: BooleanInput;

  @ContentChildren(MwcMenuItemDirective, { descendants: true })
  listOfMwcMenuItemDirective!: QueryList<MwcMenuItemDirective>;
  @ContentChildren(MwcSubMenuComponent, { descendants: true }) listOfMwcSubMenuComponent!: QueryList<MwcSubMenuComponent>;
  @Input() mwcInlineIndent = 24;
  @Input() mwcTheme: MwcMenuThemeType = 'light';
  @Input() mwcMode: MwcMenuModeType = 'inline';
  @Input() @InputBoolean() mwcInlineCollapsed = false;
  @Input() @InputBoolean() mwcSelectable = !this.isMenuInsideDropDown;
  @Output() readonly mwcClick = new EventEmitter<MwcMenuItemDirective>();
  actualMode: MwcMenuModeType = 'inline';
  dir: Direction = 'ltr';
  private inlineCollapsed$ = new BehaviorSubject<boolean>(this.mwcInlineCollapsed);
  private mode$ = new BehaviorSubject<MwcMenuModeType>(this.mwcMode);
  private destroy$ = new Subject();
  private listOfOpenedMwcSubMenuComponent: MwcSubMenuComponent[] = [];

  setInlineCollapsed(inlineCollapsed: boolean): void {
    this.mwcInlineCollapsed = inlineCollapsed;
    this.inlineCollapsed$.next(inlineCollapsed);
  }

  updateInlineCollapse(): void {
    if (this.listOfMwcMenuItemDirective) {
      if (this.mwcInlineCollapsed) {
        this.listOfOpenedMwcSubMenuComponent = this.listOfMwcSubMenuComponent.filter(submenu => submenu.mwcOpen);
        this.listOfMwcSubMenuComponent.forEach(submenu => submenu.setOpenStateWithoutDebounce(false));
      } else {
        this.listOfOpenedMwcSubMenuComponent.forEach(submenu => submenu.setOpenStateWithoutDebounce(true));
        this.listOfOpenedMwcSubMenuComponent = [];
      }
    }
  }

  constructor(
    private mwcMenuService: MenuService,
    @Inject(MwcIsMenuInsideDropDownToken) public isMenuInsideDropDown: boolean,
    private cdr: ChangeDetectorRef,
    @Optional() private directionality: Directionality
  ) {}

  ngOnInit(): void {
    combineLatest([this.inlineCollapsed$, this.mode$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([inlineCollapsed, mode]) => {
        this.actualMode = inlineCollapsed ? 'vertical' : mode;
        this.mwcMenuService.setMode(this.actualMode);
        this.cdr.markForCheck();
      });
    this.mwcMenuService.descendantMenuItemClick$.pipe(takeUntil(this.destroy$)).subscribe(menu => {
      this.mwcClick.emit(menu);
      if (this.mwcSelectable && !menu.mwcMatchRouter) {
        this.listOfMwcMenuItemDirective.forEach(item => item.setSelectedState(item === menu));
      }
    });

    this.dir = this.directionality.value;
    this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe((direction: Direction) => {
      this.dir = direction;
      this.mwcMenuService.setMode(this.actualMode);
      this.cdr.markForCheck();
    });
  }

  ngAfterContentInit(): void {
    this.inlineCollapsed$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateInlineCollapse();
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { mwcInlineCollapsed, mwcInlineIndent, mwcTheme, mwcMode } = changes;
    if (mwcInlineCollapsed) {
      this.inlineCollapsed$.next(this.mwcInlineCollapsed);
    }
    if (mwcInlineIndent) {
      this.mwcMenuService.setInlineIndent(this.mwcInlineIndent);
    }
    if (mwcTheme) {
      this.mwcMenuService.setTheme(this.mwcTheme);
    }
    if (mwcMode) {
      this.mode$.next(this.mwcMode);
      if (!changes.mwcMode.isFirstChange() && this.listOfMwcSubMenuComponent) {
        this.listOfMwcSubMenuComponent.forEach(submenu => submenu.setOpenStateWithoutDebounce(false));
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
